import { faroTableComparator } from "@components/common/faro-table/faro-table-utils";
import { TransactionProject } from "@components/table/transactions/transaction-project";
import { TransactionUser } from "@components/table/transactions/transaction-user";
import { TransactionsHeaders, getAmountSymbol, getTransactionAmount, getTransactionDescription, isConsumedTransaction, isExpiredTransaction, isFailedOrRejectedTransaction, shouldShowIncrement } from "@components/table/transactions/transactions-table-utils";
import { Chip, Tooltip } from "@flat-ui";
import { Box } from "@mui-wrapper";
import { gridStringOrNumberComparator } from "@mui/x-data-grid-pro";
import { CREDIT_DISPLAY_NAME } from "@src/constants/credit-constants";
import { withEllipsis } from "@styles/common-styles";
import { nounPluralize } from "@utils/data-display";
import { resolveUserIdentity } from "@utils/user-utils";
/** Return all the possible columns for Transactions table */
export function getTransactionsColumns({
  formatDate,
  membersMap,
  projectsMap
}) {
  return {
    [TransactionsHeaders.date]: {
      field: TransactionsHeaders.date,
      flex: 0.5,
      minWidth: 180,
      renderCell: params => {
        return <var>{formatDate(params.row.entity.timestamp)}</var>;
      },
      valueGetter: (_, row) => Date.parse(row.entity.timestamp),
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by package
      sortable: true,
      sortComparator: (v1, v2, cp1, cp2) => faroTableComparator(v1, v2, cp1, cp2, gridStringOrNumberComparator)
    },
    [TransactionsHeaders.user]: {
      field: TransactionsHeaders.user,
      flex: 1,
      minWidth: 220,
      renderCell: params => {
        const userIdentity = resolveUserIdentity(params.row.entity.context.userId, params.row.entity.context.userEmail);
        const member = userIdentity ? membersMap.get(userIdentity) : undefined;
        return <TransactionUser member={member} />;
      },
      valueGetter: (_, row) => {
        const userIdentity = resolveUserIdentity(row.entity.context.userId, row.entity.context.userEmail);
        const member = userIdentity ? membersMap.get(userIdentity) : undefined;
        return member?.name ?? userIdentity;
      },
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by package
      sortable: true,
      sortComparator: (v1, v2, cp1, cp2) => faroTableComparator(v1, v2, cp1, cp2, gridStringOrNumberComparator)
    },
    [TransactionsHeaders.description]: {
      field: TransactionsHeaders.description,
      flex: 1,
      minWidth: 220,
      renderCell: params => <Tooltip component="div" title={getTransactionDescription(params.row.entity)} sx={{
        ...withEllipsis
      }}>
          {getTransactionDescription(params.row.entity)}
        </Tooltip>,
      valueGetter: (_, row) => getTransactionDescription(row.entity),
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by package
      sortable: true,
      sortComparator: (v1, v2, cp1, cp2) => faroTableComparator(v1, v2, cp1, cp2, gridStringOrNumberComparator)
    },
    [TransactionsHeaders.project]: {
      field: TransactionsHeaders.project,
      flex: 1.3,
      minWidth: 220,
      renderCell: params => {
        return <TransactionProject projectId={params.row.entity.context.projectId ?? undefined} />;
      },
      valueGetter: (_, row) => {
        return projectsMap.get(row.entity.context.projectId || "")?.name;
      },
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by package
      sortable: true,
      sortComparator: (v1, v2, cp1, cp2) => faroTableComparator(v1, v2, cp1, cp2, gridStringOrNumberComparator)
    },
    [TransactionsHeaders.credits]: {
      field: TransactionsHeaders.credits,
      flex: 0.5,
      minWidth: 180,
      renderCell: params => {
        const isIncrease = shouldShowIncrement(params.row.entity);
        const tokenAmount = getTransactionAmount(params.row.entity);
        return <Chip disabled={isFailedOrRejectedTransaction(params.row.entity)} label={<Box>
                <var style={{
            fontWeight: "inherit"
          }}>
                  {getAmountSymbol(params.row.entity)}
                  {}
                </var>
                {nounPluralize({
            shouldShowCounter: true,
            counter: tokenAmount,
            word: CREDIT_DISPLAY_NAME
          })}
              </Box>} size="small" variant="filled" color={isIncrease ? "success" : "default"} />;
      },
      valueGetter: (_, row) => {
        const tokenAmount = getTransactionAmount(row.entity);

        // Return negative value if the transaction is related to consumption, otherwise positive value
        return isConsumedTransaction(row.entity) || isExpiredTransaction(row.entity) ? -Math.abs(tokenAmount) : Math.abs(tokenAmount);
      },
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by package
      sortable: true,
      sortComparator: (v1, v2, cp1, cp2) => faroTableComparator(v1, v2, cp1, cp2, gridStringOrNumberComparator)
    }
  };
}